import { udCompareEditionApiIndexOrder } from "utils/udCompareEditionOrder";

export const prepareAZZKUTopTable = table => {
   
   const columns = [
      { name: "number", label: "Lp." },
      { name: "profession", label: "Zawód" },
      ...( table.regions.map(( r ) => ({
         name: r.voivodeship_name,
         label: r.voivodeship_name
      })))
   ];

   const professions = !!table.regions[0]?.professions?.length 
      ? table.regions[0]?.professions
      : table.regions[1]?.professions;

   const data = professions?.map(( prof, i ) => ({
      number: i + 1,
      profession: prof.profession_name,
      [ table.regions[0]?.voivodeship_name ]: table.regions[0]?.professions?.[i]?.value,
      [ table.regions[1]?.voivodeship_name ]: table.regions[1]?.professions?.[i]?.value
   }));

   return { heading: table.variable_name, columns, data: data.slice( 0, 10 )}
}

export const prepareAZZKUTable = (table, variableName = null, territoryName = null) => {
   const columns = [
      { name: "profession", label: "Zawód" },
      ...( table.series.map(( s, i ) => ({
         name: s.name,
         label: s.name
      })))
   ] 

   const data = table.categories.map(( c, i ) => ({
      profession: c,
      ...table.series.reduce(( res, s ) => {
         res[ s.name ] = s.data[i] === null ? "b/d" : s.data[i]
         return res;
      }, {})
   }))

   // Buduj heading: nazwa wskaźnika w pierwszym wierszu, terytorium w drugim
   // Format: "variableName | territoryName" - DownloadSection podzieli to na osobne linie
   // Priorytetyzuj table.voivodeship_name nad territoryName, aby uniknąć duplikacji
   // gdy każda tabela reprezentuje konkretne województwo
   let heading;
   const indicatorName = table.variable_name || variableName || table.voivodeship_name;
   
   // Użyj table.voivodeship_name jeśli istnieje (każda tabela ma swoje województwo)
   // W przeciwnym razie użyj territoryName (np. dla "Polska" lub gdy nie ma konkretnego województwa)
   const finalTerritoryName = table.voivodeship_name || territoryName;
   
   if (finalTerritoryName && indicatorName) {
      heading = `${indicatorName} | ${finalTerritoryName}`;
   } else if (indicatorName) {
      heading = indicatorName;
   } else {
      heading = finalTerritoryName || table.voivodeship_name;
   }

   return { heading, columns, data }
}

export const prepareUDTable = (table, variableName = null, territoryName = null, isCompareMode = false, edition1 = null, edition2 = null) => {

   let columns, data, footer;

   const firstColumnLabel = table.table_header || "";

   // W trybie porównania: wiersze to edycje, kolumny to opcje zmiennej kluczowej (edycje chronologicznie)
   if (isCompareMode && edition1 && edition2) {
      const editionLabels = [String(edition1), String(edition2)];
      const compareApiOrder = udCompareEditionApiIndexOrder(edition1, edition2);
      const editionsForRows = compareApiOrder.map((i) => editionLabels[i]);
      
      columns = [
         { name: "label", label: firstColumnLabel },
         ...( table.columns.map(( c, i ) => ({
            name: "data-item" + i,
            label: c.label || `Opcja ${i + 1}`
         }))),
         { name: "sum", label: "Suma" }
      ];
      
      data = editionsForRows.map(( editionName, rowIdx ) => {
         const rowData = {
            label: String(editionName || '')
         };
         
         let rowTotalCount = 0;
         let rowTotalPercentage = 0;
         let hasValidRowData = false;
         
         const editionIndex = compareApiOrder[rowIdx];
         
         table.columns.forEach(( c, optionIndex ) => {
            const countArray = Array.isArray(c.count) ? c.count : (c.count !== undefined ? [c.count] : []);
            const percentageArray = Array.isArray(c.percentage) ? c.percentage : (c.percentage !== undefined ? [c.percentage] : []);
            
            const current = countArray[editionIndex];
            const percentageValue = percentageArray[editionIndex];
            
            // W trybie porównania, jeśli count jest stringiem typu "1679 (2020)", wyciągnij tylko liczbę
            let countValue = current;
            if (typeof countValue === 'string' && countValue.includes('(')) {
               const match = countValue.match(/^(\d+)/);
               if (match) {
                  countValue = match[1];
               }
            }
            
            rowData[ "data-item" + optionIndex ] = current === null || current === undefined ? "b/d" : `${ percentageValue !== undefined ? percentageValue : 0 }% (${ countValue})`;
            
            // Oblicz sumę dla tego wiersza
            if (current !== null && current !== undefined) {
               let countValueRaw = current;
               if (typeof countValueRaw === 'string' && countValueRaw.includes('(')) {
                  const match = countValueRaw.match(/^(\d+)/);
                  if (match) {
                     countValueRaw = match[1];
                  }
               }
               const countValueNum = Number(countValueRaw);
               const percentageValueNum = percentageValue !== undefined ? Number(percentageValue) : 0;
               if (!isNaN(countValueNum) && !isNaN(percentageValueNum)) {
                  rowTotalCount += countValueNum;
                  rowTotalPercentage += percentageValueNum;
                  hasValidRowData = true;
               }
            }
         });
         
         rowData.sum = hasValidRowData ? `${Math.round(rowTotalPercentage)}% (${rowTotalCount})` : "b/d";
         
         return rowData;
      });
      
      footer = null;
   } else if (!table.legend) {
      // Przypadek bez legendy (tylko dla trybu single)
      columns = [
         { name: "label", label: firstColumnLabel },
         { name: "count", label: "% (n)" }
      ];

      data = table.columns.map( c => ({
         label: c.label,
         count: c.count[0] === null ? "b/d" : `${ c.percentage[0]}% (${ c.count[0]})`
      }))

      // Oblicz sumy dla tabeli bez legendy
      let totalCount = 0;
      let totalPercentage = 0;
      let hasValidData = false;

      table.columns.forEach( c => {
         const countValue = Number(c.count[0]);
         const percentageValue = Number(c.percentage[0]);
         if (c.count[0] !== null && !isNaN(countValue) && !isNaN(percentageValue)) {
            totalCount += countValue;
            totalPercentage += percentageValue;
            hasValidData = true;
         }
      });

      footer = hasValidData ? ["Suma", `${Math.round(totalPercentage)}% (${totalCount})`] : null;
   } else {
      // Standardowy tryb: wiersze to opcje zmiennej kluczowej, kolumny to wartości z legendy
      const legendArray = Array.isArray(table.legend) ? table.legend : [];
      
      columns = [
         { name: "label", label: firstColumnLabel },
         ...( legendArray.map(( label, i ) => ({
            name: "data-item" + i,
            label: String(label || '')
         }))),
         { name: "sum", label: "Suma" }
      ];

      data = table.columns.map( c => {
         const rowData = {
            label: c.label
         };
         
         const countArray = Array.isArray(c.count) ? c.count : (c.count !== undefined ? [c.count] : []);
         const percentageArray = Array.isArray(c.percentage) ? c.percentage : (c.percentage !== undefined ? [c.percentage] : []);
         
         // Mapuj wartości dla każdej pozycji w legendzie
         legendArray.forEach((_, i) => {
            const current = countArray[i];
            const percentageValue = percentageArray[i];
            rowData[ "data-item" + i ] = current === null || current === undefined ? "b/d" : `${ percentageValue !== undefined ? percentageValue : 0 }% (${ current })`;
         });

         // Oblicz sumę dla tego wiersza
         let rowTotalCount = 0;
         let rowTotalPercentage = 0;
         let hasValidRowData = false;
         
         legendArray.forEach((_, i) => {
            if (countArray[i] !== null && countArray[i] !== undefined) {
               const countValue = Number(countArray[i]);
               const percentageValue = percentageArray[i] !== undefined ? Number(percentageArray[i]) : 0;
               if (!isNaN(countValue) && !isNaN(percentageValue)) {
                  rowTotalCount += countValue;
                  rowTotalPercentage += percentageValue;
                  hasValidRowData = true;
               }
            }
         });

         rowData.sum = hasValidRowData ? `${Math.round(rowTotalPercentage)}% (${rowTotalCount})` : "b/d";

         return rowData;
      });
      
      footer = null;
   }

   // Heading rendered exactly like in charts: prefer title, then label, then voivodeship_name
   // Fallback to variableName | label only if none of the above exist
   const headingFromChart = table.title || table.label || table.voivodeship_name;
   const heading = headingFromChart || (variableName ? `${variableName} | ${table.label}` : table.label);

   return { heading, columns, data, footer }
}
